//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

import { SystemService } from '../../../../services/system'
import { Provider, ProviderScope, MapEvent, MapLegendItem } from './provider'
import { OpMarker, OpCoord, OpPolygon, OpRange, OpBounds, OpTooltip, OpPolyline, Marker } from '../../../../models/custom/op-map-area'
import { Enums } from '../../../../models/common/enums'

type LngLatLike = [number, number]

const DEFAULT_OWNER = 'aiut.t8pHxM'
const DEFAULT_MAP_ID = 'pec_szczecin'
const DEFAULT_URL = 'https://api.maps.aiut.com'

export class SmartgeomaticProvider extends Provider {
	private map: any
	private mapPromise: JQueryPromise<any>
	private mapboxMap: any
	private element: any

	private markersDataset: any

	private markers: OpMarker[]

	private selectedMarkers: OpMarker[]

	private popup

	private flyToOptions = {
		speed: 1.7,
	}

	private cameraOptions = {
		padding: 30,
		maxZoom: 20,
	}

	constructor(private $scope: ProviderScope, element: HTMLElement, onMapReady: (provider: Provider) => void, systemService: SystemService) {
		super($scope, systemService)
		const _URL: string = $scope.options.initParams.tryGetValue(Enums.SimaxOperatorSetting.ProfileMapProviderParamUrl)
		const _OWNER: string = $scope.options.initParams.tryGetValue(Enums.SimaxOperatorSetting.ProfileMapProviderParamOwner)
		const _MAP_ID: string = $scope.options.initParams.tryGetValue(Enums.SimaxOperatorSetting.ProfileMapProviderParamMapId)
		const opts = {
			container: element,
			center: [$scope.options.initCenter.longitude, $scope.options.initCenter.latitude],
			zoom: $scope.options.initZoom,
			sdk: {
				baseUrl: _URL != null ? _URL : DEFAULT_URL,
			},
		}
		const {
			owner = _OWNER != null ? _OWNER : DEFAULT_OWNER,
			mapId = _MAP_ID != null ? _MAP_ID : DEFAULT_MAP_ID,
			link = _URL != null ? _URL : DEFAULT_URL
		} = {}
		this.element = element
		this.markersDataset = window['opalSdk'].createDataset('markers', {
			data: SmartgeomaticProvider.recomputeMarkers(),
		})
		const url: string = `${link}/${owner}/${mapId}`
		const authenticator = window['opalSdk'].MapAuthenticator.fromUrl(url)
		this.mapPromise = window['opalSdk'].createMap(authenticator, opts) as JQueryPromise<any>
		this.mapPromise
			.then((map) => (this.map = map))
			.then((map: any) => {
				const promise = $.Deferred<any>()
				map.event$.subscribe((event) => {
					if (event.type === 'load') {
						onMapReady(this)
						this.handleMapLoad(event)
						promise.resolve(map)
					}
					if (event.type === 'click') {
						this.handleMapClick(event)
					}
				})

				return promise
			})
	}

	private handleMapClick(event) {
		const { target: map, point, lngLat } = (event || {}).data
		const features = map.queryRenderedFeatures(SmartgeomaticProvider.boundingBoxWithOffset(point), { layers: ['markers', 'selected.markers'] })
		const head = _.first(features) as any
		if (!head) {
			const handler = this.$scope.options.onMapClick
			const coord = new OpCoord()

			coord.latitude = lngLat.lat
			coord.longitude = lngLat.lng

			handler && handler(coord)

			return
		}
		const clickedMarker = _.find(this.markers || [], (marker) => head.properties.id === marker.id)
		if (!clickedMarker) return
		this.selectedMarkers = [clickedMarker]
		this.openPopup(lngLat, clickedMarker.tooltip)
		const geojson = SmartgeomaticProvider.recomputeMarkers(this.markers, [clickedMarker])
		this.markersDataset.setData(geojson)
		clickedMarker.onClick && clickedMarker.onClick(clickedMarker)
	}

	private handleMapLoad(event) {
		// unselected markers layer
		this.map.addData(this.markersDataset, {
			id: 'markers',
			type: 'symbol',
			layout: {
				'icon-image': 'default.1:marker-red-15',
				'icon-allow-overlap': true,
				'icon-anchor': 'bottom',
			},
			paint: {
				'icon-opacity': 0.5,
			},
			filter: ['!=', 'selected', true],
		})
		// selected markers layer
		this.map.addData(this.markersDataset, {
			id: 'selected.markers',
			type: 'symbol',
			layout: {
				'icon-image': 'default.1:marker-red-15',
				'icon-allow-overlap': true,
				'icon-anchor': 'bottom',
				'icon-size': 1.5,
			},
			filter: ['==', 'selected', true],
		})
		this.mapboxMap = event.data.target
		angular.element(this.element).on('resize', () => this.resize())
	}

	private static toLngLatLike(coord: OpCoord) {
		return [coord.longitude, coord.latitude] as LngLatLike
	}

	private static toFeature(marker: OpMarker, selected = false) {
		const { coords, tooltip, id, referenceValue, type } = marker
		return {
			type: 'Feature',
			properties: {
				id,
				referenceValue,
				type,
				tooltip,
				selected,
			},
			geometry: {
				type: 'Point',
				coordinates: SmartgeomaticProvider.toLngLatLike(coords),
			},
		}
	}

	private static mergeMarkers(selected: OpMarker[] = [], unselected: OpMarker[] = []) {
		return {
			type: 'FeatureCollection',
			features: _.union(
				selected.map((m) => SmartgeomaticProvider.toFeature(m, true)),
				unselected.map((m) => SmartgeomaticProvider.toFeature(m, false))
			),
		}
	}

	private static recomputeMarkers(current: OpMarker[] = [], selected: OpMarker[] = []) {
		const selectedRefs = selected.map((marker) => marker.id)
		const asSelected = current.filter((currentMarker) => _.contains(selectedRefs, currentMarker.id))
		const asUnselected = _.difference(current, selected)
		return SmartgeomaticProvider.mergeMarkers(asSelected, asUnselected)
	}

	private static computeBoundingBox(coords: OpCoord[]) {
		const min = coords.reduce(
			(acc, curr) => ({
				lon: Math.min(curr.longitude, acc.lon),
				lat: Math.min(curr.latitude, acc.lat),
			}),
			{ lon: 180, lat: 85 }
		)
		const max = coords.reduce(
			(acc, curr) => ({
				lon: Math.max(curr.longitude, acc.lon),
				lat: Math.max(curr.latitude, acc.lat),
			}),
			{ lon: -180, lat: -85 }
		)
		return [min, max]
	}

	private static degreesToRadians(degrees: number): number {
		const radians = degrees % 360
		return (radians * Math.PI) / 180
	}

	private static boundingBoxWithOffset(point: { x: number; y: number }, offset: number = 5) {
		const { x, y } = point
		return [
			[x - offset, y - offset],
			[x + offset, y + offset],
		]
	}

	private decorateMarker(marker: OpMarker): OpMarker {
		const hasValidCoords = this.checkLatitude(marker.coords.latitude) && this.checkLongitude(marker.coords.longitude)

		if (!hasValidCoords) return null

		const markerDecorator: Marker = {
			isShown: () => {
				return _.find((this.markers || []).concat(this.selectedMarkers || []), (item) => marker.id === item.id) !== undefined
			},
			show: () => { },
			hide: () => { },
			setZIndex: (index: number) => { },
			addListener: (event: Enums.SimaxMarkerEvent, listener: (event) => void) => { },
			getPosition: () => marker.coords,
			setIcon: (icon: string, scaledSize: { width: number; height: number } = null) => { },
			getMapMarker: () => {
				return null
			},
			setPosition: (position: OpCoord) => {
				marker.coords = position
				this.markersDataset.setData(SmartgeomaticProvider.recomputeMarkers(this.markers))
			},
		}

		marker.setMarker(markerDecorator)

		return marker
	}

	private openPopup(lngLat: LngLatLike, content: string) {
		this.closePopup()
		this.popup = window['opalSdk'].createPopup().setLngLat(lngLat).setHTML(`<p> ${content} </p>`)
		this.map.addPopup(this.popup)
	}

	private closePopup() {
		this.popup && this.popup.remove()
		this.popup = undefined
	}

	public setCenter(coord: OpCoord) {
		// console.log('sg:provider:setCenter', coord);
		const center = SmartgeomaticProvider.toLngLatLike(coord)

		if (this.map) {
			this.map.flyTo({ center })
		}
		// this.map!.flyTo(opts);
	}

	public getCenter() {
		// console.log('sg:provider:getCenter')
		if (!this.map) return null
		const [Longitude, Latitude] = this.map.center as LngLatLike
		const object = {
			Latitude,
			Longitude,
		}
		return new OpCoord(object)
	}

	public center() {
		// console.log('sg:provider:center');
		if (!this.map) return
		const bbox = SmartgeomaticProvider.computeBoundingBox(this.markers.map((marker) => marker.coords))
		const options = this.map.cameraForBounds(bbox, this.cameraOptions)
		this.map.jumpTo(options)
	}

	public centerOnMarkers(markers: OpMarker[], animate: boolean): void {
		// console.log('sg:provider:centerOnMarkers', markers, animate);
		const bbox = SmartgeomaticProvider.computeBoundingBox(markers.map((marker) => marker.coords))
		const options = this.map.cameraForBounds(bbox, this.cameraOptions)
		animate === true ? this.map.flyTo(_.extend(options, this.flyToOptions)) : this.map.jumpTo(options)
	}

	public setZoom(zoom: number) {
		// console.log('sg:provider:setZoom', zoom);
		if (this.map) {
			this.map.jumpTo({ zoom })
		}
	}

	public getZoom() {
		// console.log('sg:provider:getZoom');
		return this.map ? this.map.zoom : null
	}

	public zoomOnMarkers(markers: OpMarker[], animate: boolean): void {
		// console.log('sg:provider:zoomOnMarkers', markers, animate);

		this.closePopup()
		let bbox
		const currentMarkers = this.markers || []
		if (!markers || markers.length === 0) {
			this.selectedMarkers = []
			bbox = SmartgeomaticProvider.computeBoundingBox(currentMarkers.map((marker) => marker.coords))
		} else {
			this.selectedMarkers = markers
			bbox = SmartgeomaticProvider.computeBoundingBox(markers.map((marker) => marker.coords))
		}
		const geojson = SmartgeomaticProvider.recomputeMarkers(currentMarkers, markers)
		this.markersDataset.setData(geojson)
		const options = this.map.cameraForBounds(bbox, this.cameraOptions)
		animate === true ? this.map.flyTo(_.extend(options, this.flyToOptions)) : this.map.jumpTo(options)
	}

	public addMarker(marker: OpMarker): OpMarker {
		// console.log('sg:provider:addMarker', marker);
		const decorated = this.decorateMarker(marker)
		if (!decorated) return null
		const markers = this.markers || []
		markers.push(decorated)
		this.markers = markers
		this.markersDataset.setData(SmartgeomaticProvider.recomputeMarkers(this.markers))
		return decorated
	}

	public clearMarkers(): void {
		// console.log('sg:provider:clearMarkers');

		if (!this.map) return

		this.markers = []
		this.selectedMarkers = []
		this.markersDataset.setData(SmartgeomaticProvider.recomputeMarkers(this.markers))
	}

	public setMarkers(markers: OpMarker[], zoom?: boolean, center?: boolean) {
		// console.log('sg:provider:setMarkers', markers, zoom, center);

		if (!this.map) return

		this.markers = markers || []
		const selectedRefs = (this.selectedMarkers || []).map((marker) => marker.id)
		this.selectedMarkers = this.markers.filter((marker) => _.contains(selectedRefs, marker.id))
		const geojson = SmartgeomaticProvider.recomputeMarkers(this.markers, this.selectedMarkers)
		this.markersDataset.setData(geojson)
	}

	public setRoute(markers: OpMarker[]): void { }

	public setSelectedMarkers(markers: OpMarker[], animate: boolean, zoom?: boolean) {
		// console.log('sg:provider:setSelectedMarkers', markers, animate, zoom);
		this.zoomOnMarkers(markers, animate)
	}

	public addPolygon(polygon: OpPolygon): OpPolygon {
		// console.log('sg:provider:addPolygon', polygon);
		return null
	}

	public clearPolygons(): void {
		// console.log('sg:provider:clearPolygons');
	}

	public setPolygons(polygons: OpPolygon[]) {
		// console.log('sg:provider:setPolygons', polygons);
	}

	public addRange(range: OpRange): OpRange {
		// console.log('sg:provider:addRange', range);
		return null
	}

	public clearRanges(): void {
		// console.log('sg:provider:clearRanges');
	}

	public setRanges(ranges: OpRange[]) {
		// console.log('sg:provider:setRanges', ranges);
	}

	public addPolyline(polyline: OpPolyline): OpPolyline {
		// console.log('sg:provider:addPolyline', polyline);
		return null
	}

	public computeDistanceBetweenCoords(c1: OpCoord, c2: OpCoord): number {
		// console.log('sg:provider:computeDistanceBetweenCoords', c1, c2);
		const from = SmartgeomaticProvider.toLngLatLike(c1)
		const to = SmartgeomaticProvider.toLngLatLike(c2)

		const dLat = SmartgeomaticProvider.degreesToRadians(to[1] - from[1])
		const dLon = SmartgeomaticProvider.degreesToRadians(to[0] - from[0])
		const lat1 = SmartgeomaticProvider.degreesToRadians(from[1])
		const lat2 = SmartgeomaticProvider.degreesToRadians(to[1])

		const a = Math.pow(Math.sin(dLat / 2), 2) + Math.pow(Math.sin(dLon / 2), 2) * Math.cos(lat1) * Math.cos(lat2)

		const earthRadius = 6371008.8 // earth radius in meters
		const inRadians = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
		// convert to meters => radians * earthRadius
		return inRadians * earthRadius
	}

	public zoomCenter() {
		// console.log('sg:provider:zoomCenter');
		if (!this.map) return

		if (this.markers && this.markers.length > 0) {
			const bbox = SmartgeomaticProvider.computeBoundingBox(this.markers.map((marker) => marker.coords))
			const options = this.map.cameraForBounds(bbox, this.cameraOptions)
			this.map.jumpTo(options)
		} else {
			const center = SmartgeomaticProvider.toLngLatLike(this.$scope.options.initCenter)
			const zoom = this.$scope.options.initZoom
			this.map.jumpTo({ center, zoom })
		}
	}

	zoomIn() {
		// console.log('sg:provider:zoomIn');
		if (!this.map) return
		const selectedMarkers = this.selectedMarkers || []
		const bbox = SmartgeomaticProvider.computeBoundingBox(selectedMarkers.map((marker) => marker.coords))
		const options = this.map.cameraForBounds(bbox, this.cameraOptions)
		this.map.jumpTo(options)
	}

	getBounds(): OpBounds {
		// console.log('sg:provider:getBounds');
		if (!this.map) return
		const [west, south, east, north] = this.map.bounds
		return new OpBounds(new OpCoord({ Longitude: west, Latitude: south }), new OpCoord({ Longitude: east, Latitude: north }))
	}

	setBounds(bounds: OpBounds) {
		// console.log('sg:provider:setBounds', bounds);
		if (!this.map) return
		if (!bounds) return
		const sw = SmartgeomaticProvider.toLngLatLike(bounds.southWest)
		const ne = SmartgeomaticProvider.toLngLatLike(bounds.northEast)
		const bbox = [sw, ne]

		const options = this.map.cameraForBounds(bbox, { padding: 30 })
		this.map.jumpTo(options)
	}

	fitBounds(positions: OpCoord[]) {
		// console.log('sg:provider:fitBounds', positions);
		if (!this.map) return

		const bbox = SmartgeomaticProvider.computeBoundingBox(positions)
		const options = this.map.cameraForBounds(bbox, this.cameraOptions)
		this.map.jumpTo(options)
	}

	disableMap() {
		// console.log('sg:provider:disableMap');
	}

	enableMap() {
		// console.log('sg:provider:enableMap');
	}

	clearListeners(event: MapEvent, marker: OpMarker) {
		// console.log('sg:provider:clearListeners', event, marker);
	}

	createMarkerTooltip(marker: OpMarker, text: string): OpTooltip {
		// console.log('sg:provider:createMarkerTooltip', marker, text);
		return null
	}

	setLegend(legendItems: MapLegendItem[]) {
		// console.log('sg:provider:setLegend', legendItems);
	}

	enableAreaSelect(callback: (bounds) => void): void {
		// console.log('sg:provider:enableAreaSelect');
	}

	disableAreaSelect(): void {
		// console.log('sg:provider:disableAreaSelect');
	}

	resize(): void {
		// console.log('sg:provider:resize');
		// use setTImeout to sync with angular
		// first angular have to render changed parent element to DOM
		// second map should resize to a changed dimensions
		setTimeout(() => this.mapboxMap && this.mapboxMap.resize(), 0)
	}
}
